import { useState, useEffect } from 'react';
import Calendar from './Calendar';

interface AfterHoursRequestFormProps {
  selectedArea: string | null;
  submitRequest: (startDateTime: number, endDateTime: number, reason: string) => void;
}

// Helper function to get next 15-minute rounded time
const getNextQuarterHour = () => {
  const now = new Date();
  const minutes = now.getMinutes();
  const roundedMinutes = Math.ceil(minutes / 15) * 15;
  const hours = now.getHours() + Math.floor(roundedMinutes / 60);
  const finalMinutes = roundedMinutes % 60;
  return `${hours.toString().padStart(2, '0')}:${finalMinutes.toString().padStart(2, '0')}`;
};

// Generate 15-minute increment time options
const generateTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      options.push(timeString);
    }
  }
  return options;
};

// Generate duration options in 0.5 hour increments up to 6 hours
const generateDurationOptions = () => {
  const options = [];
  for (let i = 0.5; i <= 6; i += 0.5) {
    options.push(i);
  }
  return options;
};

export default function AfterHoursRequestForm({ selectedArea, submitRequest }: AfterHoursRequestFormProps) {
  const [form, setForm] = useState<{ date: Date; start: string; duration: number; reason: string }>({ 
    date: new Date(), 
    start: getNextQuarterHour(), 
    duration: 1, 
    reason: '' 
  });

  // Update start time to next quarter hour when component mounts or date changes
  useEffect(() => {
    const now = new Date();
    const selectedDate = new Date(form.date);
    
    // If selected date is today, use next quarter hour, otherwise default to 09:00
    if (selectedDate.toDateString() === now.toDateString()) {
      setForm(prev => ({ ...prev, start: getNextQuarterHour() }));
    } else {
      setForm(prev => ({ ...prev, start: '18:00' }));
    }
  }, [form.date]);

  const submit = async () => {
    if (!form.date || !form.start || !form.duration) return;
    const start = new Date(form.date.getFullYear(), form.date.getMonth(), form.date.getDate(), parseInt(form.start.split(':')[0]), parseInt(form.start.split(':')[1]));
    const end = new Date(start.getTime() + (form.duration * 60 * 60 * 1000)); // Add duration in milliseconds
    submitRequest(start.getTime(), end.getTime(), form.reason);
    setForm({ date: new Date(), start: getNextQuarterHour(), duration: 1, reason: '' });
  };

  // Generate summary text
  const getSummaryText = () => {
    if (!selectedArea || !form.date || !form.start) return '';
    
    const startTime = new Date(form.date.getFullYear(), form.date.getMonth(), form.date.getDate(), parseInt(form.start.split(':')[0]), parseInt(form.start.split(':')[1]));
    const formattedDate = form.date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
    const formattedTime = startTime.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    });
    
    const durationText = form.duration === 1 ? '1 hour' : 
                        form.duration < 1 ? `${form.duration * 60} minutes` :
                        form.duration % 1 === 0 ? `${form.duration} hours` :
                        `${form.duration} hours`;
    
    return `Request for ${selectedArea} on ${formattedDate} @ ${formattedTime} for ${durationText}.`;
  };

  const timeOptions = generateTimeOptions();
  const durationOptions = generateDurationOptions();

  return (
    <div className="ah-card">
      <div className="ah-title">New After Hours Request</div>
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 16,
        width: '100%'
      }}>
        {/* Calendar Section */}
        <div style={{ width: '100%' }}>
          <label className="muted" style={{ display: 'block', marginBottom: 8 }}>Select Date</label>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center',
            width: '100%'
          }}>
            <Calendar
              selectedDate={form.date}
              onDateSelect={(date) => setForm({ ...form, date })}
              maxDaysFuture={29}
            />
          </div>
        </div>

        {/* Form Fields */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 12,
          width: '100%'
        }}>
          <div>
            <label className="muted" style={{ display: 'block', marginBottom: 4, fontSize: '14px' }}>Start Time</label>
            <select 
              className="ah-input" 
              value={form.start} 
              onChange={e => setForm({ ...form, start: e.target.value })}
              style={{ 
                width: '100%',
                minHeight: '44px', // Touch-friendly height
                fontSize: '16px' // Prevents zoom on iOS
              }}
            >
              {timeOptions.map(time => (
                <option key={time} value={time}>{time}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="muted" style={{ display: 'block', marginBottom: 4, fontSize: '14px' }}>Duration</label>
            <select 
              className="ah-input" 
              value={form.duration} 
              onChange={e => setForm({ ...form, duration: parseFloat(e.target.value) })}
              style={{ 
                width: '100%',
                minHeight: '44px', // Touch-friendly height
                fontSize: '16px' // Prevents zoom on iOS
              }}
            >
              {durationOptions.map(duration => (
                <option key={duration} value={duration}>
                  {duration === 1 ? '1 hour' : 
                   duration < 1 ? `${duration * 60} minutes` :
                   duration % 1 === 0 ? `${duration} hours` :
                   `${duration} hours`}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Reason field - full width */}
        <div style={{ width: '100%' }}>
          <label className="muted" style={{ display: 'block', marginBottom: 4, fontSize: '14px' }}>Reason</label>
          <input 
            className="ah-input" 
            type="text" 
            placeholder="Optional" 
            value={form.reason} 
            onChange={e => setForm({ ...form, reason: e.target.value })}
            style={{ 
              width: '100%',
              minHeight: '44px', // Touch-friendly height
              fontSize: '16px' // Prevents zoom on iOS
            }}
          />
        </div>
        
        {/* Summary Text */}
        {getSummaryText() && (
          <div style={{ 
            padding: '12px 16px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px', 
            fontSize: '14px',
            color: '#495057',
            border: '1px solid #e9ecef',
            lineHeight: '1.4',
            textAlign: 'center',
            width: '100%',
            boxSizing: 'border-box'
          }}>
            {getSummaryText()}
          </div>
        )}
        
        {/* Submit button - full width on mobile */}
        <div style={{ width: '100%' }}>
          <button 
            className="ah-btn" 
            onClick={submit}
            style={{ 
              width: '100%',
              minHeight: '48px', // Touch-friendly height
              fontSize: '16px',
              fontWeight: '500'
            }}
          >
            Submit Request
          </button>
        </div>
      </div>
    </div>
  );
}
